import { useId, useRef, type ReactNode } from 'react';
import { useModalDialog } from '../../lib/useModalDialog';
import { XIcon } from '../icons';

interface ModalProps {
  /** Rendered in the modal header together with a close button. Omit for a header-less modal. */
  title?: ReactNode;
  /** Called for the close button, Escape and (optionally) backdrop clicks. Unmount the modal in response. */
  onClose: () => void;
  children: ReactNode;
  /** Extra classes for the dialog card, e.g. a wider max width. */
  className?: string;
  /** Set to false while an action is running to block Escape, backdrop and close-button dismissal. */
  dismissible?: boolean;
  /** Forms default to false so a stray click does not discard input. */
  closeOnBackdrop?: boolean;
}

/**
 * Modal built on the native `<dialog>` element. Mount it only while it should be open.
 * Children usually contain `.modal-body` and `.modal-footer` sections.
 */
export function Modal({
  title,
  onClose,
  children,
  className = '',
  dismissible = true,
  closeOnBackdrop = false,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useModalDialog(dialogRef);

  return (
    <dialog
      ref={dialogRef}
      className={`modal-dialog ${className}`.trim()}
      aria-labelledby={title ? titleId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        if (dismissible) onClose();
      }}
      // Browsers may force-close after repeated Escape presses; keep React state in sync.
      onClose={onClose}
      onClick={(event) => {
        if (closeOnBackdrop && dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      {title ? (
        <div className="modal-header">
          <h2 id={titleId} className="font-display">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="admin-action-btn"
            aria-label="Schliessen"
            disabled={!dismissible}
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      ) : null}
      {children}
    </dialog>
  );
}
